import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  RefreshCw, Users, Video, CalendarCheck, ScanFace, MonitorSmartphone, ListChecks,
  CheckCircle2, AlertTriangle, XCircle, Info, CalendarRange,
} from 'lucide-react';
import MainLayout from '../../../components/layout/MainLayout';
import { Skeleton } from '../../../components/ui/Skeleton';
import { useToast } from '../../../contexts/ToastContext';
import { obtenerEstadisticasUsoApp, type EstadisticasUsoApp, type PorPlataforma } from '../../../services/usoAppService';
import { mensajeError } from '../correo/errores';
import { hoyPanama, haceDiasPanama } from '../kyc/formato';
import styles from './UsoAppModule.module.css';
import { agrupar, nombrePantalla, AREAS, fechaCorta, nf, type Granularidad } from './usoAppFormato';
import {
  Indicador, EscalaAnidada, BarraPlataformas, GraficaTendencia, Embudo, Resultados, Ranking, Panel,
  type Metrica,
} from './UsoAppPiezas';

const tot = (p?: PorPlataforma) => (p?.ios ?? 0) + (p?.android ?? 0);
const PRESETS = [7, 30, 90] as const;

export default function UsoAppPage() {
  const { addToast } = useToast();
  const [desde, setDesde] = useState(haceDiasPanama(29));
  const [hasta, setHasta] = useState(hoyPanama());
  const [preset, setPreset] = useState<number | null>(30);
  const [gran, setGran] = useState<Granularidad>('dia');
  const [datos, setDatos] = useState<EstadisticasUsoApp | null>(null);
  const [cargando, setCargando] = useState(true);

  const reqId = useRef(0);
  const cargar = useCallback(async (d = desde, h = hasta) => {
    const id = ++reqId.current;
    setCargando(true);
    try {
      const r = await obtenerEstadisticasUsoApp({ desde: d, hasta: h });
      if (id === reqId.current) setDatos(r);
    } catch (err) {
      if (id === reqId.current) addToast('error', mensajeError(err));
    } finally {
      if (id === reqId.current) setCargando(false);
    }
  }, [desde, hasta, addToast]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, []);

  const aplicarPreset = (dias: number) => {
    const d = haceDiasPanama(dias - 1);
    const h = hoyPanama();
    setDesde(d); setHasta(h); setPreset(dias);
    cargar(d, h);
  };

  const r = useMemo(() => {
    if (!datos) return null;
    const s = datos.serie;
    const suma = (tipo: string) => s.reduce((a, d) => a + tot(d.eventos[tipo]), 0);
    const sumaPlat = (tipo: string) => s.reduce((a, d) => ({
      ios: a.ios + (d.eventos[tipo]?.ios ?? 0), android: a.android + (d.eventos[tipo]?.android ?? 0),
    }), { ios: 0, android: 0 });
    const etiqueta = (tipo: string) => datos.eventos[tipo] ?? tipo;

    const ultimoDia = s[s.length - 1];
    const ultimaSemana = datos.activosSemana[datos.activosSemana.length - 1];
    const ultimoMes = datos.activosMes[datos.activosMes.length - 1];
    const hoy = hoyPanama();
    const esHoy = ultimoDia?.fecha === hoy;

    const activos = gran === 'dia'
      ? agrupar(s, 'dia', (d) => ({ ios: d.activos.ios ?? 0, android: d.activos.android ?? 0 }))
      : (gran === 'semana' ? datos.activosSemana : datos.activosMes).map((p) => ({
        etiqueta: gran === 'semana' && p.desde ? `Sem. ${p.desde.slice(8)}/${p.desde.slice(5, 7)}` : p.periodo,
        ios: p.ios ?? 0, android: p.android ?? 0,
      }));

    const metricas: Metrica[] = [
      { clave: 'activos', nombre: 'Usuarios activos', descripcion: 'Personas distintas que abrieron la app, por plataforma. No se suman entre períodos.', datos: activos },
      { clave: 'logins', nombre: 'Inicios de sesión', descripcion: 'Veces que un paciente inició sesión en la app.', datos: agrupar(s, gran, (d) => ({ ios: d.eventos.login?.ios ?? 0, android: d.eventos.login?.android ?? 0 })) },
      { clave: 'aperturas', nombre: 'Aperturas de la app', descripcion: 'Veces que se abrió la app (una sesión nueva).', datos: agrupar(s, gran, (d) => ({ ios: d.eventos.sesion_inicio?.ios ?? 0, android: d.eventos.sesion_inicio?.android ?? 0 })) },
      { clave: 'cuentas', nombre: 'Cuentas nuevas', descripcion: 'Cuentas creadas en el período, según el registro de usuarios.', datos: agrupar(s, gran, (d) => ({ valor: d.cuentasNuevas })), unica: true },
    ];

    const pasosDe = (tipos: string[]) => tipos.map((t) => ({ etiqueta: etiqueta(t), valor: suma(t) }));

    const otrasAreas = AREAS.filter((a) => !['Teleconsulta', 'Citas', 'Registro biométrico'].includes(a.area));

    return {
      hoyEtiqueta: esHoy ? 'Hoy' : (ultimoDia ? `El ${fechaCorta(ultimoDia.fecha)}` : 'Último día'),
      semanaEtiqueta: esHoy ? 'Esta semana' : 'Última semana del período',
      mesEtiqueta: esHoy ? 'Este mes' : 'Último mes del período',
      activosHoy: ultimoDia?.activos.total ?? tot(ultimoDia?.activos),
      activosSemana: ultimaSemana?.total ?? 0,
      activosMes: ultimoMes?.total ?? 0,
      platMes: { ios: ultimoMes?.ios ?? 0, android: ultimoMes?.android ?? 0 },
      platLogins: sumaPlat('login'),
      logins: suma('login'),
      aperturas: suma('sesion_inicio'),
      cuentas: s.reduce((a, d) => a + d.cuentasNuevas, 0),
      teleconsultas: s.reduce((a, d) => a + (d.teleconsultas ?? 0), 0),
      kyc: s.reduce((a, d) => a + (d.kyc?.total ?? 0), 0),
      kycAprobados: s.reduce((a, d) => a + (d.kyc?.aprobadas ?? 0), 0),
      sparks: {
        activos: s.map((d) => d.activos.total ?? tot(d.activos)),
        logins: s.map((d) => tot(d.eventos.login)),
        aperturas: s.map((d) => tot(d.eventos.sesion_inicio)),
        cuentas: s.map((d) => d.cuentasNuevas),
        teleconsultas: s.map((d) => d.teleconsultas ?? 0),
        kyc: s.map((d) => d.kyc?.total ?? 0),
      },
      metricas,
      embudoTeleconsulta: pasosDe(['teleconsulta_triage_completado', 'teleconsulta_solicitada', 'teleconsulta_llamada_iniciada', 'teleconsulta_finalizada']),
      embudoCitas: pasosDe(['cita_solicitud_iniciada', 'cita_creada']),
      cancelaciones: { sistema: suma('cita_cancelada'), paciente: suma('cita_cancelada_paciente') },
      kycIniciados: suma('kyc_iniciado'),
      kycResultados: { aprobado: suma('kyc_aprobado'), revision: suma('kyc_revision_manual'), rechazado: suma('kyc_rechazado') },
      otras: otrasAreas.flatMap((a) => a.eventos.map((t) => ({ area: a.area, tipo: t, etiqueta: etiqueta(t), ...sumaPlat(t) })))
        .sort((a, b) => (b.ios + b.android) - (a.ios + a.android)),
      todas: AREAS.flatMap((a) => a.eventos.map((t) => ({ area: a.area, tipo: t, etiqueta: etiqueta(t), ...sumaPlat(t) }))),
    };
  }, [datos, gran]);

  const fidelidad = r && r.activosMes > 0 ? Math.round((r.activosHoy / r.activosMes) * 100) : null;
  const actualizado = datos?.actualizadoEn
    ? new Date(datos.actualizadoEn).toLocaleString('es-PA', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })
    : null;

  return (
    <MainLayout pageTitle="Uso de la app" pageSubtitle="Cómo usan los pacientes la app móvil: quiénes vuelven, en qué plataforma y qué funciones aprovechan.">
      <div className={styles.raiz}>
        {/* ── Filtros ── */}
        <div className={styles.filtros}>
          <div className={styles.filtroGrupo}>
            <span className={styles.filtroEtiqueta}>Período</span>
            <div className={styles.segmentado} role="group" aria-label="Período">
              {PRESETS.map((d) => (
                <button key={d} type="button" aria-pressed={preset === d} disabled={cargando}
                  onClick={() => aplicarPreset(d)}>{d} días</button>
              ))}
            </div>
            <div className={styles.rango}>
              <CalendarRange size={15} aria-hidden="true" />
              <label className={styles.srOnly} htmlFor="uso-desde">Desde</label>
              <input id="uso-desde" type="date" value={desde} max={hasta}
                onChange={(e) => { setDesde(e.target.value); setPreset(null); }} />
              <span aria-hidden="true">–</span>
              <label className={styles.srOnly} htmlFor="uso-hasta">Hasta</label>
              <input id="uso-hasta" type="date" value={hasta} min={desde} max={hoyPanama()}
                onChange={(e) => { setHasta(e.target.value); setPreset(null); }} />
            </div>
          </div>
          <div className={styles.filtroGrupo}>
            <span className={styles.filtroEtiqueta}>Agrupar por</span>
            <div className={styles.segmentado} role="group" aria-label="Agrupar por">
              {([['dia', 'Día'], ['semana', 'Semana'], ['mes', 'Mes']] as const).map(([g, n]) => (
                <button key={g} type="button" aria-pressed={gran === g} onClick={() => setGran(g)}>{n}</button>
              ))}
            </div>
          </div>
          <div className={styles.filtroFin}>
            {actualizado && <span className={styles.actualizado}>Datos al {actualizado}</span>}
            <button type="button" className={styles.botonActualizar} onClick={() => cargar()} disabled={cargando}
              aria-label="Actualizar datos">
              <RefreshCw size={15} className={cargando ? styles.girando : undefined} />
              <span>{preset === null ? 'Aplicar fechas' : 'Actualizar'}</span>
            </button>
          </div>
        </div>

        {cargando && !datos ? (
          <div className={styles.cargando} aria-busy="true" aria-label="Cargando estadísticas">
            <Skeleton variant="card" height={190} />
            <div className={styles.indicadores}>
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="card" height={112} />)}
            </div>
            <Skeleton variant="card" height={380} />
          </div>
        ) : !datos || !r ? (
          <div className={styles.errorCarga}>
            No se pudieron cargar las estadísticas. Revise la conexión y use «Actualizar».
          </div>
        ) : (
          <div className={cargando ? styles.recargando : undefined}>
            {!datos.recoleccionDesde ? (
              <div className={styles.banner} role="note">
                <Info size={16} aria-hidden="true" />
                <p>
                  La app todavía no reporta uso en este ambiente: sesiones, pantallas y funciones llegan con la próxima
                  versión publicada. Mientras tanto se muestran las cuentas, la actividad reciente, las teleconsultas y
                  los registros biométricos, que no dependen de la versión.
                </p>
              </div>
            ) : datos.recoleccionDesde > desde ? (
              <div className={styles.banner} role="note">
                <Info size={16} aria-hidden="true" />
                <p>La app reporta uso desde el {fechaCorta(datos.recoleccionDesde)}; antes de esa fecha esas cifras aparecen en cero.</p>
              </div>
            ) : null}

            {/* ── Alcance ── */}
            <section className={styles.alcance} aria-labelledby="alcance-t">
              <div className={styles.alcancePrincipal}>
                <h2 id="alcance-t" className={styles.alcanceTitulo}>Pacientes que usan la app</h2>
                <div className={styles.alcanceCifras}>
                  {[
                    [r.hoyEtiqueta, r.activosHoy],
                    [r.semanaEtiqueta, r.activosSemana],
                    [r.mesEtiqueta, r.activosMes],
                  ].map(([e, v]) => (
                    <div key={String(e)} className={styles.alcanceCifra}>
                      <span className={styles.alcanceNumero}>{nf.format(Number(v))}</span>
                      <span className={styles.alcanceEtiqueta}>{e}</span>
                    </div>
                  ))}
                </div>
                <EscalaAnidada unidad="personas" escalones={[
                  { etiqueta: r.hoyEtiqueta, valor: r.activosHoy },
                  { etiqueta: r.semanaEtiqueta, valor: r.activosSemana },
                  { etiqueta: r.mesEtiqueta, valor: r.activosMes },
                ]} />
                <p className={styles.alcanceNota}>
                  {fidelidad === null
                    ? 'Personas distintas que abrieron la app en cada ventana.'
                    : <>De cada 100 personas activas en el mes, <b>{fidelidad}</b> abrieron la app {r.hoyEtiqueta === 'Hoy' ? 'hoy' : r.hoyEtiqueta.toLowerCase()}.</>}
                </p>
              </div>
              <div className={styles.alcanceLado}>
                <h3 className={styles.ladoTitulo}><MonitorSmartphone size={16} aria-hidden="true" /> Plataformas</h3>
                <div className={styles.ladoBloque}>
                  <span className={styles.ladoEtiqueta}>Usuarios activos del mes</span>
                  <BarraPlataformas {...r.platMes} />
                </div>
                <div className={styles.ladoBloque}>
                  <span className={styles.ladoEtiqueta}>Inicios de sesión del período</span>
                  <BarraPlataformas {...r.platLogins} />
                </div>
              </div>
            </section>

            {/* ── Indicadores del período ── */}
            <div className={styles.indicadores}>
              <Indicador etiqueta="Inicios de sesión" valor={r.logins} serie={r.sparks.logins} tono="var(--color-primary)" pie="en el período" />
              <Indicador etiqueta="Aperturas de la app" valor={r.aperturas} serie={r.sparks.aperturas} tono="var(--serie-ios)" pie="sesiones nuevas" />
              <Indicador etiqueta="Cuentas nuevas" valor={r.cuentas} serie={r.sparks.cuentas} tono="var(--brand-gold-600)" pie="registradas en el período" />
              <Indicador etiqueta="Teleconsultas" valor={r.teleconsultas} serie={r.sparks.teleconsultas} tono="var(--color-primary)" pie="en el período" />
              <Indicador etiqueta="Registros biométricos" valor={r.kyc} serie={r.sparks.kyc} tono="var(--serie-ios)"
                pie={<>{nf.format(r.kycAprobados)} aprobados{r.kyc > 0 && <> · {Math.round((r.kycAprobados / r.kyc) * 100)}%</>}</>} />
            </div>

            <GraficaTendencia metricas={r.metricas} />

            {/* ── Recorridos ── */}
            <div className={styles.rejilla2}>
              <Panel titulo="Recorrido de teleconsulta" icono={<Video size={16} />}
                nota="Veces que se completó cada paso. El porcentaje compara con el paso anterior: muestra dónde se queda la gente.">
                <Embudo pasos={r.embudoTeleconsulta} />
              </Panel>
              <Panel titulo="Citas presenciales" icono={<CalendarCheck size={16} />}
                nota="Solicitudes que terminaron en una cita, y cancelaciones del período.">
                <Embudo pasos={r.embudoCitas} />
                <div className={styles.cancelaciones}>
                  <div><span>Canceladas por el paciente</span><b>{nf.format(r.cancelaciones.paciente)}</b></div>
                  <div><span>Canceladas por el sistema</span><b>{nf.format(r.cancelaciones.sistema)}</b></div>
                </div>
              </Panel>
            </div>

            <div className={styles.rejilla2}>
              <Panel titulo="Registro biométrico" icono={<ScanFace size={16} />}
                nota={<>Resultados de la validación de identidad reportados por la app. Se iniciaron <b>{nf.format(r.kycIniciados)}</b> registros.</>}>
                <Resultados items={[
                  { etiqueta: 'Aprobados', valor: r.kycResultados.aprobado, tono: 'ok', icono: <CheckCircle2 size={15} /> },
                  { etiqueta: 'En revisión manual', valor: r.kycResultados.revision, tono: 'warn', icono: <AlertTriangle size={15} /> },
                  { etiqueta: 'Rechazados', valor: r.kycResultados.rechazado, tono: 'err', icono: <XCircle size={15} /> },
                ]} />
              </Panel>
              <Panel titulo="Actividad de las cuentas" icono={<Users size={16} />}
                nota="Según la última sesión de cada cuenta; no depende de la versión de la app.">
                {datos.auth ? (
                  <EscalaAnidada unidad="cuentas" escalones={[
                    { etiqueta: 'Últimas 24 h', valor: datos.auth.d1 },
                    { etiqueta: 'Últimos 7 días', valor: datos.auth.d7 },
                    { etiqueta: 'Últimos 30 días', valor: datos.auth.d30 },
                    { etiqueta: 'Cuentas de pacientes', valor: datos.auth.pacientes },
                  ]} />
                ) : (
                  <div className={styles.graficaVacia}>El cálculo nocturno de actividad aún no ha corrido.</div>
                )}
              </Panel>
            </div>

            {/* ── Pantallas y funciones ── */}
            <div className={styles.rejilla2}>
              <Panel titulo="Pantallas más visitadas" icono={<MonitorSmartphone size={16} />}
                nota="Vistas por pantalla, con su parte de Android y de iOS.">
                {datos.topPantallas.length === 0 ? (
                  <div className={styles.graficaVacia}>Sin vistas de pantalla reportadas en el período.</div>
                ) : (
                  <Ranking filas={datos.topPantallas.map((p) => ({ clave: p.nombre, nombre: nombrePantalla(p.nombre), titulo: p.nombre, ios: p.ios, android: p.android }))} />
                )}
              </Panel>
              <Panel titulo="Cuenta, perfil y notificaciones" icono={<ListChecks size={16} />}
                nota="Otras acciones del período.">
                <Ranking inicial={6} filas={r.otras.map((f) => ({ clave: f.tipo, nombre: f.etiqueta, titulo: f.area, ios: f.ios, android: f.android }))} />
              </Panel>
            </div>

            <details className={styles.detalle}>
              <summary>Ver el detalle de todas las acciones</summary>
              <div className={styles.tablaScroll}>
                <table className={styles.tabla}>
                  <thead><tr><th scope="col">Área</th><th scope="col">Acción</th><th scope="col" className={styles.num}>Android</th><th scope="col" className={styles.num}>iOS</th><th scope="col" className={styles.num}>Total</th></tr></thead>
                  <tbody>
                    {r.todas.map((f, i) => (
                      <tr key={f.tipo} className={i > 0 && r.todas[i - 1].area !== f.area ? styles.filaGrupo : undefined}>
                        <td className={styles.area}>{i === 0 || r.todas[i - 1].area !== f.area ? f.area : ''}</td>
                        <td>{f.etiqueta}</td>
                        <td className={styles.num}>{nf.format(f.android)}</td>
                        <td className={styles.num}>{nf.format(f.ios)}</td>
                        <td className={styles.num}><b>{nf.format(f.ios + f.android)}</b></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>

            <p className={styles.aviso}>
              Datos de este ambiente. No se guarda qué hizo cada paciente: solo contadores por día y plataforma.
              {datos.auth && <> La actividad de las cuentas se calculó el {new Date(datos.auth.calculadoEn).toLocaleString('es-PA')}.</>}
            </p>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
